import {Navigation} from "@/components/Navigation";
import {Footer} from "@/components/Footer";
import {Categories} from "@/components/Categories";
import {Search} from "lucide-react";
import {Input} from "@/components/ui/input";
import {PollListItem} from "@/components/PollListItem";
import {useQuery} from "@tanstack/react-query";
import {Poll, PollStatus} from "@/types/Poll";
import React, {useEffect, useRef, useState} from "react";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {debounce} from 'lodash';
import {useSearchParams} from "react-router-dom";
import {SEO} from "@/components/SEO.tsx";

const SearchPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const query = searchParams.get('q');
    const [searchTerm, setSearchTerm] = useState(query || "");
    const [selectedCategory, setSelectedCategory] = useState("all");
    const [sortBy, setSortBy] = useState("endDate"); // endDate, prize

    const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

    const clearQuery = () => {
        searchParams.delete('q');
        setSearchParams(searchParams);
    };

    const {data: polls, isLoading, refetch} = useQuery({
        queryKey: ['polls', searchTerm, selectedCategory, sortBy],
        queryFn: async () => {
            const queryParams = buildQueryString({
                search: searchTerm,
                category: selectedCategory,
                order: sortBy,
            });

            const url = `${import.meta.env.VITE_API_URL}/polls${queryParams}`;
            // console.log("url:", url)
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const data: Poll[] = await response.json();
            return data
                //.filter(poll => parseInt(poll.status.toString()) === PollStatus.active);
        },
        enabled: false
    });

    useEffect(() => {
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        debounceTimeout.current = setTimeout(() => {
            if (searchTerm !== null || selectedCategory !== null || sortBy) {
                clearQuery();
                refetch();
            }
        }, 500);

        return () => {
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }
        };
    }, [searchTerm, selectedCategory, sortBy, refetch]);

    const buildQueryString = (params: { [key: string]: string | number }) => {
        const queryString = Object.entries(params)
            .filter(([key, value]) => value !== null && value !== undefined && value !== "" && value !== "all")
            .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
            .join('&');

        return queryString ? `?${queryString}` : '';
    }

    const filteredPolls = polls?.filter(poll => {
        const matchesSearch = poll.title.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = selectedCategory === 'all' || poll.categories.slug === selectedCategory;
        return matchesSearch && matchesCategory;
    });

    const sortedPolls = [...(filteredPolls || [])].sort((a, b) => {
        if (sortBy === "endDate") {
            return new Date(b.endDate).getTime() - new Date(a.endDate).getTime();
        }
        return b.prize - a.prize;
    });

    return (
        <div className="min-h-screen bg-white dark:bg-background font-sans">
            <SEO
                title="Search"
                description="Search polls, users, categories, and more across the platform"
                type="website"
                name="blankpoll"
                imageUrl="/blankpoll-banner.jpg"
            />
            <Navigation/>
            <Categories/>

            <main className="max-w-7xl mx-auto px-4 py-8 mt-12">
                <div className="mb-8 space-y-4">
                    <h1 className="text-2xl font-bold text-secondary dark:text-white">Search Results</h1>

                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="relative flex-1">
                            <Search
                                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 h-4 w-4"/>
                            <Input
                                type="text"
                                placeholder="Search polls..."
                                className="pl-10 w-full bg-white dark:bg-gray-700 dark:text-white dark:border-gray-600"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        <div className="flex gap-4">
                            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                                <SelectTrigger className="w-[180px] bg-white dark:bg-gray-700 dark:text-white dark:border-gray-600">
                                    <SelectValue placeholder="Select category"/>
                                </SelectTrigger>
                                <SelectContent className="bg-white dark:bg-gray-800">
                                    <SelectItem value="all" className="dark:text-gray-200 dark:focus:bg-gray-700 dark:hover:bg-gray-700">All Categories</SelectItem>
                                    <SelectItem value="politics" className="dark:text-gray-200 dark:focus:bg-gray-700 dark:hover:bg-gray-700">Politics</SelectItem>
                                    <SelectItem value="news" className="dark:text-gray-200 dark:focus:bg-gray-700 dark:hover:bg-gray-700">News</SelectItem>
                                    <SelectItem value="sports" className="dark:text-gray-200 dark:focus:bg-gray-700 dark:hover:bg-gray-700">Sports</SelectItem>
                                    <SelectItem value="fun" className="dark:text-gray-200 dark:focus:bg-gray-700 dark:hover:bg-gray-700">Entertainment</SelectItem>
                                    <SelectItem value="economy" className="dark:text-gray-200 dark:focus:bg-gray-700 dark:hover:bg-gray-700">Economy</SelectItem>
                                    <SelectItem value="technology" className="dark:text-gray-200 dark:focus:bg-gray-700 dark:hover:bg-gray-700">Technology</SelectItem>
                                    <SelectItem value="trending" className="dark:text-gray-200 dark:focus:bg-gray-700 dark:hover:bg-gray-700">Trending</SelectItem>
                                </SelectContent>
                            </Select>

                            <Select value={sortBy} onValueChange={setSortBy}>
                                <SelectTrigger className="w-[180px] bg-white dark:bg-gray-700 dark:text-white dark:border-gray-600">
                                    <SelectValue placeholder="Sort by"/>
                                </SelectTrigger>
                                <SelectContent className="bg-white dark:bg-gray-800">
                                    <SelectItem value="endDate" className="dark:text-gray-200 dark:focus:bg-gray-700 dark:hover:bg-gray-700">End Date</SelectItem>
                                    <SelectItem value="prize" className="dark:text-gray-200 dark:focus:bg-gray-700 dark:hover:bg-gray-700">Prize Amount</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </div>

                {isLoading ? (
                    <div className="max-w-7xl mx-auto px-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-12">
                            {[1, 2, 3, 4].map((i) => (
                                <div key={i} className="h-64 bg-gray-100 dark:bg-gray-800 rounded-lg animate-pulse"/>
                            ))}
                        </div>
                    </div>
                ) : sortedPolls?.length === 0 ? (
                    <div className="text-center py-8">
                        <p className="text-gray-500 dark:text-gray-400">No polls found matching your criteria</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                        {sortedPolls?.map((poll) => (
                            <PollListItem key={poll.id} poll={poll}/>
                        ))}
                    </div>
                )}

                <div className="mt-4 text-center">
                    <p className="text-gray-500 dark:text-gray-400">Showing {sortedPolls?.length} results</p>
                </div>
            </main>

            <Footer/>
        </div>
    );
};

export default SearchPage;
